import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import LedDisplay from '../../components/LedDisplay/LedDisplay';
import { galleryAPI, homeCatsAPI } from '../../utils/api';
import styles from './Index.module.css';

// ── Datos ─────────────────────────────────────────────────────────────────────


const CATEGORIES = [
  {
    to:    '/productos/colectivos',
    img:   '/img/home/First_3/Linea_71_Frante.jpg',
    alt:   'Carteles Led para Colectivos',
    title: 'Colectivos y Transporte',
    desc:  'Ramaleros Full Color, simple y doble línea. Programables por WiFi.',
    tag:   'Transporte',
  },
  {
    to:    '/productos/turneros',
    img:   '/img/productos/Turno_Puesto/Turno/U300_MICROLED/U300_MICROLED_1.jpg',
    alt:   'Turneros Led Microled',
    title: 'Turneros con Llamado de Fila',
    desc:  'Turno único y turno-puesto para múltiples filas. Control remoto incluido.',
    tag:   'Gestión',
  },
  {
    to:    '/LandingPage/Estaciones-de-servicio',
    img:   '/img/productos/tarifadores_de_Precios/Estacion_de_Servicio/Tari_2.jpeg',
    alt:   'Carteles LED para Estaciones de Servicio',
    title: 'Estaciones de Servicio',
    desc:  'Tarifadores de precios y cartelería interior programable.',
    tag:   'Combustible',
  },
  {
    to:    '/productos/farmacias',
    img:   '/img/productos/Cruces_Led/Forma_Cruz_Chica/Forma_Cruz_Chica.jpg',
    alt:   'Cruz Led para Farmacias',
    title: 'Cruces LED para Farmacias',
    desc:  'Alta visibilidad diurna y nocturna. Standard y Full Color con animaciones.',
    tag:   'Salud',
  },
  {
    to:    '/productos/estacionamientos',
    img:   '/img/home/Dot-Estacionamiento 4.jpg',
    alt:   'Señalización de Estacionamiento',
    title: 'Estacionamientos y Parking',
    desc:  'Tótems, contadores de lugares disponibles y señalética interna.',
    tag:   'Parking',
  },
  {
    to:    '/productos/mercado_de_valores',
    img:   '/img/home/bolsa_de_comercio.jpg',
    alt:   'Pantalla LED para Finanzas',
    title: 'Finanzas y Casas de Cambio',
    desc:  'Indicadores de divisas y cotizaciones en tiempo real.',
    tag:   'Finanzas',
  },
];

const GALLERY_DEFAULT = [
  { img: '/img/home/bocaDeSubte.jpg',               cap: 'Estación de Subte'              },
  { img: '/img/home/Santander.jpg',                 cap: 'Banco Santander Río'            },
  { img: '/img/home/peajes autopistas del sol.jpg', cap: 'Peaje Autopista Panamericana'   },
  { img: '/img/home/tapate_la_boca_linea_h.jpg',    cap: 'Andén Subte Línea H'            },
  { img: '/img/home/Dot-Estacionamiento 4.jpg',     cap: 'Shopping Dot — Estacionamiento' },
  { img: '/img/home/camion_reciclaje.jpg',          cap: 'Pantalla LED Reciclado'         },
  { img: '/img/home/totem-rural.JPG',               cap: 'Estacionamiento La Rural'       },
];

const WHY = [
  { icon: '🏭', title: 'Empresa argentina',      desc: 'Diseño y ensamblado propio. Más de 30 años en el mercado local.' },
  { icon: '🔧', title: 'Soporte técnico local',  desc: 'Servicio post-venta y repuestos disponibles en Argentina.' },
  { icon: '🎨', title: 'Diseño a medida',        desc: 'Cada producto se fabrica según las medidas y requerimientos del cliente.' },
];

// ── Componente ────────────────────────────────────────────────────────────────

export default function Index() {
  const [gallery,     setGallery]     = useState(GALLERY_DEFAULT);
  const [categories,  setCategories]  = useState(CATEGORIES);

  useEffect(() => {
    galleryAPI.getAll()
      .then(data => { if (Array.isArray(data) && data.length > 0) setGallery(data); })
      .catch(() => {});
    homeCatsAPI.getAll()
      .then(data => { if (Array.isArray(data) && data.length > 0) setCategories(data); })
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>▷ Pantallas Led — Microled Argentina 🔴</title>
        <meta name="description" content="Fabricamos pantallas LED y cartelería electrónica para transporte, farmacias, estaciones de servicio, comercios y más. +30 años de experiencia. Instalación y garantía incluida." />
        <meta name="theme-color" content="#CB3234" />
        <link rel="canonical" href="https://microled.com.ar/" />
        <meta property="og:title"       content="▷ Pantallas Led — Microled Argentina 🔴" />
        <meta property="og:description" content="Fabricamos pantallas LED para transporte, farmacias, estaciones de servicio y más. +30 años de experiencia en Argentina." />
        <meta property="og:image"       content="https://microled.com.ar/img/home/tapate_la_boca_linea_h.jpg" />
        <meta property="og:url"         content="https://microled.com.ar/" />
        <meta property="og:type"        content="website" />
      </Helmet>

      <h1 className="sr-only">Pantallas Led — Microled Argentina</h1>

      {/* ═══════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════════ */}
      <section
        className={styles.hero}
        style={{ backgroundImage: `url('/img/franjas/franja_index.jpg')` }}
      >
        <div className={styles.heroOverlay} />

        <div className={styles.heroInner}>
          {/* Columna izquierda — texto */}
          <div className={styles.heroText}>
            <p className={styles.eyebrow}>Fabricación nacional · +30 años</p>
            <h2 className={styles.heroTitle}>
              Señalización con
              <span className={styles.heroTitleRed}>Pantallas LED</span>
            </h2>
            <p className={styles.heroSubtitle}>
              Letreros electrónicos para transporte, comercios, farmacias,
              estacionamientos y más. Más de 30 años de experiencia.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/productos" className={styles.ctaPrimary}>Ver productos</Link>
              <Link to="/contacto"  className={styles.ctaSecondary}>Solicitar presupuesto</Link>
            </div>
          </div>

          {/* Columna derecha — display LED */}
          <div className={styles.heroLed}>
            <LedDisplay />
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          PRODUCTOS
      ════════════════════════════════════════════════════ */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionPre}>Catálogo</span>
          <h2>Nuestros productos</h2>
          <p>Cartelería LED a medida para cada rubro</p>
        </div>

        <div className={styles.catGrid}>
          {categories.map(cat => (
            <Link key={cat.to} to={cat.to} className={styles.catCard}>
              <div className={styles.catTag}>{cat.tag}</div>
              <div className={styles.catImg}>
                <img src={cat.img} alt={cat.alt} loading="lazy" />
              </div>
              <div className={styles.catBody}>
                <h3>{cat.title}</h3>
                <p>{cat.desc}</p>
                <span className={styles.catArrow}>Ver más →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          POR QUÉ MICROLED
      ════════════════════════════════════════════════════ */}
      <section className={styles.whySection}>
        <div className={styles.whyInner}>
          <div className={styles.sectionHead} style={{ textAlign: 'left', marginBottom: '2rem' }}>
            <span className={styles.sectionPre}>Diferencial</span>
            <h2>¿Por qué elegirnos?</h2>
          </div>
          <div className={styles.whyGrid}>
            {WHY.map(w => (
              <div key={w.title} className={styles.whyCard}>
                <span className={styles.whyIcon}>{w.icon}</span>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          GALERÍA
      ════════════════════════════════════════════════════ */}
      <section className={styles.gallerySection}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionPre}>Portfolio</span>
          <h2>Trabajos realizados</h2>
          <p>Instalaciones en empresas y organizaciones de todo el país</p>
        </div>

        <div className={styles.gallery} data-uk-lightbox="animation: slide">
          {gallery.map((item, i) => (
            <div key={i} className={styles.galleryItem}>
              <a href={item.img} data-caption={item.cap}>
                <img src={item.img} alt={item.cap} loading="lazy" />
                <div className={styles.galleryCap}>{item.cap}</div>
              </a>
            </div>
          ))}
        </div>
      </section>

    </>
  );
}
